// addModal.js
'use strict';
import { List } from 'immutable';
import {
  ADD_MODAL,
  ADD_MODAL_NEXT_ID,
  SET_MODAL_STATUS,
} from '../ActionTypes.js';
import { modalTemplate } from '../reducer/modals.js';
import { ANIMATION_DURATION_MSEC } from '../resource/modalConstants.js';

const defaultList = new List();

/**
 * Add modal
 * @kind action
 * @param {string} {id} - action param.
 * @param {string} [{prevId}] - previous modal id.
 * @param {array} [{higherThanIds}=[]] - model ids which this modal should higher than.
 * @param {array} [{lowerThanIds}=[]] - model ids which this modal should lower than.
 * @param {bool} [{isHigherThanAll}] - should modal higher than all existing modals.
 * @param {bool} [{isLowerThanAll}] - should modal lower than all existing modals.
 * @param {bool} [{isAlwaysHigher}] - should modal stay higher than regular modals.
 *   A child whose `prevId` is always-higher inherits this flag automatically.
 * @param {string} [{transitionStatus}] - transition status.
 * @return {Promise} Action promise.
 */
const addModal =
  ({
    id,
    prevId,
    higherThanIds = [],
    lowerThanIds = [],
    isHigherThanAll,
    isLowerThanAll,
    isAlwaysHigher = false,
    transitionStatus,
    payload,
  }) =>
  async (dispatch, getState) => {
    const modals = getState().get('modals') || defaultList;
    const prevModal = modals.get(prevId);
    const resolvedIsAlwaysHigher =
      isAlwaysHigher || prevModal?.get('isAlwaysHigher') === true;

    let shouldComputeHigher = true;
    if (
      !(higherThanIds.length || isHigherThanAll || resolvedIsAlwaysHigher) &&
      (isLowerThanAll || lowerThanIds.length)
    ) {
      shouldComputeHigher = false;
    }

    const referenceIds = shouldComputeHigher
      ? [
          ...new Set(
            [
              prevId,
              ...higherThanIds,
              ...(isHigherThanAll || resolvedIsAlwaysHigher
                ? [...modals.keys()]
                : []),
            ].filter(id => id)
          ),
        ]
      : [
          ...new Set(
            [
              prevId,
              ...lowerThanIds,
              ...(isLowerThanAll ? [...modals.keys()] : []),
            ].filter(id => id)
          ),
        ];

    const referenceZIndexes = modals
      .filter(modal => referenceIds.includes(modal.get('id')))
      .map(modal => modal.get('zIndex'))
      .toArray();

    const zIndex = shouldComputeHigher
      ? Math.max(...referenceZIndexes, 99) + 1
      : referenceZIndexes.length
        ? Math.min(...referenceZIndexes) - 1
        : 99;

    if (prevModal) {
      dispatch({
        type: ADD_MODAL_NEXT_ID,
        payload: { id: prevId, nextId: id },
      });
    }

    const modalData = {
      ...modalTemplate,
      higherThanIds: shouldComputeHigher ? referenceIds : [],
      lowerThanIds: !shouldComputeHigher ? referenceIds : [],
      id,
      zIndex,
      // Always present so the reducer stores the resolved value, including
      // `false` when reopening without direct or inherited always-higher.
      isAlwaysHigher: resolvedIsAlwaysHigher,
      payload,
    };

    if (transitionStatus) {
      setTimeout(() => {
        // Skip if status changed (e.g. closed mid-opening) to avoid overwriting back to `display`.
        const currentModal = getState().get('modals')?.get(id);
        if (!currentModal || currentModal.get('status') !== transitionStatus) {
          return;
        }
        dispatch({
          type: SET_MODAL_STATUS,
          payload: { status: modalTemplate.status, id },
        });
      }, ANIMATION_DURATION_MSEC); // TODO: remote config
      modalData.status = transitionStatus;
    }

    let zIndexUpdates = [];

    // Keep always-higher modals above the new regular modal without changing
    // the existing stacking behavior among regular modals.
    if (!resolvedIsAlwaysHigher) {
      const alwaysHigherModals = modals.filter(
        modal => modal.get('isAlwaysHigher') && modal.get('id') !== id
      );
      // Once any always-higher modal is covered, re-number ALL of them (not
      // just the covered ones), so a partial raise cannot land two of them on
      // the same z-index and leave the stacking order to DOM order.
      if (alwaysHigherModals.some(modal => modal.get('zIndex') <= zIndex)) {
        let nextZIndex = zIndex + 1;
        zIndexUpdates = alwaysHigherModals
          // Preserve their existing stacking order: the previous top modal stays top.
          .sortBy(modal => modal.get('zIndex'))
          .map(alwaysHigherModal => {
            const update = {
              id: alwaysHigherModal.get('id'),
              zIndex: nextZIndex,
            };
            nextZIndex += 1;
            return update;
          })
          .toArray();
      }
    }

    return dispatch({
      type: ADD_MODAL,
      payload: {
        modal: modalData,
        zIndexUpdates,
      },
    });
  };

export default addModal;
